import { useState, useContext, useEffect } from "react"
import { Flex, toast, Typography } from "@stardust-ds/react"
import intl from 'react-intl-universal';

import RcmpModalAdd from "../../molecules/ModalAdd"
import styles from "./index.module.scss"
import BoardContext from "../../../contexts/Board"
import RcmpActivityField from "../ActivityField"
import { FormProvider, useForm } from "react-hook-form"
import EyeIcon from "../../../assets/icons/EyeIcon"
import EyeSlashIcon from "../../../assets/icons/EyeSlashIcon"
import useDataApi from "../../../hooks/useDataApi"
import { fnHiddenBlockFields } from "../../../services/blocks"

export default function RcmpModalHideFields(props) {
  const {
    fnHandleCancel,
    fnHandleConfirm,
    bIsOpen,
    title,
    buttonCancelText,
    buttonSaveText,
    oCurrentBlock,
    nBlockViewMode,
  } = props

  const { oCurrentBoard } = useContext(BoardContext)
  const useFormMethods = useForm({})
  const [aHideFields, fnSetHideFields] = useState([])

  const [hideFieldsRes, fnHideFieldsReq] = useDataApi(fnHiddenBlockFields)

  const aBlockFields = oCurrentBoard?.board_model?.fields || []

  // set selected hide fields
  useEffect(() => {
    if (!oCurrentBlock?.opbl_nid || !bIsOpen || !aBlockFields) {
      fnSetHideFields([])
      return
    }

    if (aHideFields?.length > 0) return

    const aBlockHiddenFields =
      nBlockViewMode === 0
        ? oCurrentBlock?.table_hidden_fields
        : oCurrentBlock?.kanban_hidden_fields
    if (!aBlockHiddenFields?.length) {
      fnSetHideFields([])
      return
    }

    const aSelectedHiddenFields =
      aBlockFields
        ?.filter((f) =>
          aBlockHiddenFields.find((t) => f?.ombf_nid === t?.ophf_ombf_nid),
        )
        ?.map((f) => ({ ...f, opfd_nid: f?.field?.opfd_nid })) || []

    fnSetHideFields(aSelectedHiddenFields)
  }, [oCurrentBlock, bIsOpen])

  useEffect(() => {
    if (hideFieldsRes.loading || !hideFieldsRes.done) return

    if (hideFieldsRes.error) {
      toast({
        type: "error",
        description: intl.get("BlocksPage.hide_fields_modal.error"),
      })
      return
    }

    if (hideFieldsRes.data) {
      toast({
        type: "success",
        description: intl.get("BlocksPage.hide_fields_modal.success"),
      })

      const aTransformedHideFields =
        aHideFields?.map((item) => ({
          ophf_ombf_nid: item?.ombf_nid,
        })) || []

      fnHandleConfirm?.({
        ...oCurrentBlock,
        hidden_fields: aTransformedHideFields,
      })
    }
  }, [hideFieldsRes])

  const fnSaveHideFields = async () => {
    if (hideFieldsRes.loading) return

    const aFieldsIds = aHideFields.map((item) => item?.ombf_nid)
    const hiddenFieldFrom = nBlockViewMode === 0 ? "TABLE" : "KANBAN"
    await fnHideFieldsReq(oCurrentBlock?.opbl_nid, hiddenFieldFrom, aFieldsIds || [])
  }

  const fnHandleOnClose = () => {
    if (hideFieldsRes.loading) return

    fnHandleCancel?.()
  }

  const bAllFieldsAreHidden = aBlockFields?.length === aHideFields?.length
  const bDisableSaveButton = hideFieldsRes.loading || (nBlockViewMode === 1 && bAllFieldsAreHidden)

  return (
    <RcmpModalAdd
      isOpen={bIsOpen}
      buttonCancelText={buttonCancelText}
      buttonSaveText={buttonSaveText}
      title={title}
      fnHandleCancel={fnHandleOnClose}
      buttonType={"button"}
      oButtonSaveProps={{ disabled: bDisableSaveButton, isLoading: hideFieldsRes.loading, onClick: fnSaveHideFields }}
      oButtonCancelProps={{ disabled: hideFieldsRes.loading, onClick: fnHandleOnClose }}
    >
      <FormProvider {...useFormMethods}>
        <div className={styles.modalInfoContent}>
          <div className={styles.textHeadModalCustomFields}>
            <Typography color="#415364" type="l3" textAlign="left">
              {intl.get("BlocksPage.hide_fields_modal.description")}
            </Typography>
          </div>
          {aBlockFields.map((oField, i) => (
            <RcmpBlockField
              key={`block-hide-field-${i}`}
              field={{...oField, ...oField?.field}}
              bIsDraggable={false}
              bIsDisabled={true}
              fnSetHideFields={fnSetHideFields}
              aHideFields={aHideFields}
            />
          ))}
        </div>
      </FormProvider>
    </RcmpModalAdd>
  )
}

function RcmpBlockField(props) {
  const { fnSetHideFields, aHideFields, field } = props

  const bIsHide = Boolean(aHideFields?.find((item) => item?.opfd_nid === field?.opfd_nid))

  const fnRemoveHideFields = () => {
    const aNewHideFields = aHideFields.filter(
      (item) => item?.opfd_nid !== field?.opfd_nid,
    )
    fnSetHideFields(aNewHideFields)
  }

  const fnAddHideFields = () => {
    const aNewHideFields = [...aHideFields, field]
    fnSetHideFields(aNewHideFields)
  }

  return (
    <Flex width="100%" alignContent="center" justifyContent="center" gap="16px">
      <div style={{ width: "95%" }}>
        <RcmpActivityField {...field} />
      </div>
      <div style={{ marginTop: 18, cursor: "pointer" }}>
        {bIsHide ? (
          <EyeSlashIcon onClick={fnRemoveHideFields} fill="#FF3541" />
        ) : (
          <EyeIcon onClick={fnAddHideFields} fill="#1ECB4F" />
        )}
      </div>
    </Flex>
  )
}
